import ConfirmModal from '@/components/ConfirmModal';
import { abandonExchange } from '@/features/purchase/purchaseActions';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { TransText } from '@/i18n/trans/text';
import { persistor } from '@/store';
import { useDispatch } from '@/store/utils';
import { breakpoints } from '@/utils/breakpoints';
import { Button, Icons, Typography } from '@packages/webshop-design-system';
import { type FC, useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

// Change-booking replacement for CancelPurchaseButton: cancels the BE
// exchange operation (silent on already-confirmed ops) + resets purchase +
// change-trips state via `abandonExchange`, then purges the persisted store
// and returns to manage.
//
// `onExit` replaces the post-abandon navigation for the journey-change deep-link
// flow, which has no manage-booking context to return to. The persisted-store
// purge is manage-flow cleanup, so it's skipped with it.
interface CancelExchangeButtonProps {
  onExit?: () => void;
}

const CancelExchangeButton: FC<CancelExchangeButtonProps> = ({ onExit }) => {
  const dispatch = useDispatch();
  const { setCurrentSearchFormValues } = useSearchWidget();
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const navigate = useNavigate();

  const handleCancel = useCallback(async () => {
    await dispatch(abandonExchange());

    // Both flows seed the shared search-form context, so clear it either way.
    setCurrentSearchFormValues(undefined);

    if (onExit) {
      onExit();
      return;
    }

    void persistor.purge();
    navigate('/manage-booking/details');
  }, [dispatch, navigate, onExit, setCurrentSearchFormValues]);

  return (
    <ConfirmModal
      title={<TransText i18nKey="cancelChange" />}
      description={
        <Typography variant="paragraph">
          <TransText i18nKey="cancelChangeConfirmation" />
        </Typography>
      }
      cancelLabel="dismiss"
      confirmLabel="confirm"
      onConfirm={handleCancel}
    >
      <Button
        size={isLaptopOrBigger ? 'medium' : 'large'}
        data-testid="cancel-exchange-button"
        className="rounded-lg laptop:w-auto"
        variant="tertiary"
        startAdornment={<Icons.cancel />}
      >
        <TransText i18nKey="cancel" />
      </Button>
    </ConfirmModal>
  );
};

export default CancelExchangeButton;
